import {useState, useCallback, useEffect, useRef} from 'react';
import {Amplify} from 'aws-amplify';
import * as Auth from 'aws-amplify/auth';
import apiFetch from "@wordpress/api-fetch";
import getSetting from "../../utils/settings";

const config = getSetting('cognitoConfig');

export default function CognitoAuthenticationProvider(
    {
        Provider,
        children,
        statusActions,
        fetchCustomer,
        customer,
        ...props
    }) {
    const cognitoUser = useRef();

    Amplify.configure({
        Auth: {
            Cognito: config
        }
    });

    apiFetch.use(async (options, next) => {
        try {
            const session = await Auth.fetchAuthSession();
            if (session.tokens) {
                const token = session.tokens.accessToken.toString();
                options.headers = {...options.headers, Authorization: `Bearer ${token}`};
            }
        } catch (error) {
            console.log(error);
        }
        return next(options);
    });

    useEffect(() => {
        Auth.getCurrentUser().then(async () => {
            customer.setLoggedIn(true);
            await fetchCustomer();
        }).catch(err => {
            console.log(err);
        }).finally(() => {
            statusActions.setInitialized(true);
        });
    }, []);

    const signIn = useCallback(async (username, password) => {
        try {
            statusActions.setLoggingIn(true);
            const {isSignedIn, nextStep: {signInStep}} = await Auth.signIn({username, password});
            switch (signInStep) {
                case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                    customer.setShowMFA(true);
                    break;
                case 'RESET_PASSWORD':
                case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                    customer.setNeedsPasswordChange(true);
                    break;
                case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                    customer.setShowMFA(true);
                    break;
            }
            if (isSignedIn) {
                await fetchCustomer();
                customer.setLoggedIn(true);
                customer.setUsername(username);
            }
        } catch (error) {
            throw new Error(error?.message);
        } finally {
            statusActions.setLoggingIn(false);
        }
    }, [customer, fetchCustomer]);

    const signOut = useCallback(async () => {
        try {
            statusActions.setLoggingOut(true);
            await Auth.signOut();
        } catch (error) {
            console.log(error);
        } finally {
            customer.logout();
            statusActions.setLoggingOut(false);
        }
    }, []);

    const confirmSignIn = useCallback(async (code) => {
        try {
            const {isSignedIn} = await Auth.confirmSignIn({challengeResponse: code});
            if (isSignedIn) {
                await fetchCustomer();
                customer.setLoggedIn(true);
            }
        } catch (error) {
            throw error;
        }
    }, [customer]);

    const forgotPassword = useCallback(async (username) => {
        try {
            await Auth.resetPassword({username});
            customer.setUsername(username);
        } catch (error) {
            if (error.name === 'UserNotFoundException') {
                throw new Error('Account does not exist. If this is your first time ordering with us, an account will be created for you ' +
                    'during the checkout process.');
            }
            throw error;
        }
    }, [customer]);

    const resendConfirmation = useCallback(async (username) => {
        await Auth.resendSignUpCode({username});
    }, []);

    const register = useCallback(async (username, password, userAttributes = {}) => {
        try {
            const response = await Auth.signUp({
                username,
                password,
                options: {
                    userAttributes: {
                        email: username,
                        ...userAttributes
                    },
                    clientMetadata: {
                        source: 'CustomerApp'
                    }
                }
            });
            return response;
        } catch (error) {
            if (error.name === 'UsernameExistsException') {
                throw new Error('An account for this email address exists. Please login.');
            }
            throw error;
        }
    }, []);

    const confirmAccount = useCallback(async (username, confirmationCode) => {
        await Auth.confirmSignUp({
            username,
            confirmationCode,
            options: {
                clientMetadata: {
                    source: 'CustomerApp'
                }
            }
        });
    }, []);

    const resetPassword = useCallback(async (username, confirmationCode, newPassword) => {
        await Auth.confirmResetPassword({username, confirmationCode, newPassword});
    }, []);

    const changePassword = useCallback((oldPassword, newPassword) => {
        return Auth.getCurrentUser().then(async user => {
            try {
                await Auth.updatePassword({newPassword, oldPassword});
            } catch (error) {
                console.log(error);
                throw error;
            }
        }).catch(error => {
            throw error;
        })
    }, []);

    const confirmNewPassword = useCallback(async (password) => {
        return confirmSignIn(password);
    }, [confirmSignIn]);

    const getAuthenticatorCode = useCallback(async () => {
        try {
            const user = await Auth.getCurrentUser();
            return await Auth.setUpTOTP(user);
        } catch (error) {
            throw error;
        }
    }, []);

    const getQRCodeString = async (code) => {
        const user = await Auth.getCurrentUser();
        const totpSetupDetails = await Auth.setUpTOTP();
        const appName = 'AtlasWP';
        const setupUri = totpSetupDetails.getSetupUri(appName);
        return setupUri;
    }

    const verifyMFACode = useCallback(async (totpCode) => {
        try {
            if (!totpCode) {
                throw new Error('Code is required');
            }
            const user = await Auth.getCurrentUser();
            await Auth.verifyTOTPSetup({code: totpCode});
        } catch (error) {
            console.log('Verify Authenticator error:', error);
            throw error;
        }
    }, []);

    const context = {
        ...props,
        customer,
        signIn,
        confirmSignIn,
        signOut,
        register,
        confirmAccount,
        forgotPassword,
        resetPassword,
        resendConfirmation,
        changePassword,
        confirmNewPassword,
        verifyMFACode,
        getAuthenticatorCode,
        getQRCodeString
    }
    return <Provider value={context}>{children}</Provider>
}
